/**
 * French document strings.
 *
 * Accounting wording follows `docs/ACCOUNTING-TERMINOLOGY-GLOSSARY.md`
 * (facture, TVA, autoliquidation, HT/TTC); strings that already exist in the
 * app's `common` namespace are copied from
 * `apps/web/public/locales/fr/common.json` verbatim.
 */
export const fr = {
    common: {
        generatedOn: 'Généré le {{date}}',
        page: 'Page {{page}} sur {{total}}',
        status: 'Statut',
        project: 'Projet',
    },
    identity: {
        iban: 'IBAN',
        bic: 'BIC',
        tradingName: 'Nom commercial',
        relatedParty: 'Partie liée',
        registrationNumber: {
            NL: 'Numéro KvK',
            BE: 'Numéro BCE/KBO',
            DE: 'Handelsregister',
            FR: 'SIREN',
            GB: "Numéro d'entreprise",
            US: "Enregistrement d'État",
            DEFAULT: "Numéro d'enregistrement",
        },
        vatNumber: {
            NL: 'Numéro BTW',
            BE: 'Numéro BTW',
            DE: 'USt-IdNr',
            FR: 'Numéro de TVA',
            GB: 'Numéro de TVA',
            US: 'Numéro de taxe de vente',
            DEFAULT: 'Numéro de TVA',
        },
        taxEntityNumber: {
            NL: 'RSIN',
            US: 'EIN',
            DEFAULT: 'Numéro fiscal',
        },
        taxExempt: {
            NL: 'Statut ANBI',
            GB: 'Statut caritatif',
            US: 'Statut 501(c)(3)',
            DEFAULT: 'Exonéré de taxe',
        },
    },
    invoice: {
        titleAr: 'Facture',
        titleArDraft: 'Projet de facture',
        draftNotice: "Ce document n'est pas une facture et n'ouvre pas droit à déduction de la TVA.",
        cancelledNotice: "Annulée — cette facture a été contre-passée et aucun montant n'est dû à ce titre. Ce document est un justificatif de la facture annulée, pas un avoir.",
        titleAp: 'Facture fournisseur',
        from: 'De',
        billTo: 'Facturer à',
        billFrom: 'Facture de',
        details: 'Détails de la facture',
        draftDetails: 'Détails',
        invoiceDate: 'Date de facture',
        draftDate: 'Date',
        supplyDate: 'Date de la livraison ou de la prestation',
        dueDate: "Date d'échéance",
        cancelledOn: 'Annulée le',
        paymentTerms: 'Conditions de paiement',
        noteToCustomer: 'Note au client',
        selfBilling: 'Autofacturation',
        vatNotes: 'Mentions TVA',
        lines: {
            heading: 'Produit ou Service',
            description: 'Description',
            serviceCategory: 'Catégorie de service',
            qty: 'Qté',
            unitPrice: 'Prix',
            lineTotal: 'Sous-total',
            total: 'Total',
        },
        treatments: {
            exempt: 'Exonéré',
            reverse_charge: 'Autoliquidation',
            zero_rated: 'Taux zéro',
            non_reclaimable: 'TVA non récupérable',
        },
        taxStatements: {
            reverse_charge: 'Autoliquidation',
            non_reclaimable: 'Total TVA non récupérable incluse',
        },
        exemptionReasons: {
            art_132_1_i: "Exonéré de TVA en vertu de l'article 132(1)(i) de la directive TVA",
            art_132_1_n: "Exonéré de TVA en vertu de l'article 132(1)(n) de la directive TVA",
            art_132_1_j: "Exonéré de TVA en vertu de l'article 132(1)(j) de la directive TVA",
            export: "Exportation au taux zéro en vertu de l'article 146 de la directive TVA",
            intra_eu: "Livraison intracommunautaire au taux zéro en vertu de l'article 138 de la directive TVA",
            resale: 'Exonéré — certificat de revente au dossier',
            nonprofit: 'Exonéré — organisation à but non lucratif',
            government: 'Exonéré — entité gouvernementale',
        },
        totals: {
            subtotal: 'Sous-total HT',
            total: 'Total TTC',
            amountPaid: 'Montant payé',
            amountDue: 'Montant dû',
        },
        payments: {
            heading: 'Historique des paiements',
            entry: 'Paiement n° {{index}}',
        },
        statuses: {
            draft: 'Brouillon',
            sent: 'Envoyée',
            unpaid: 'Impayée',
            partially_paid: 'Partiellement payée',
            paid: 'Payée',
            overdue: 'En retard',
            cancelled: 'Annulée',
            pending: 'En attente',
            approved: 'Approuvée',
            rejected: 'Rejetée',
            on_hold: 'Suspendue',
        },
        thanks: 'Merci de votre confiance !',
    },
    report: {
        asOf: 'Au {{date}}',
        forPeriod: 'Pour la période du {{start}} au {{end}}',
        amountsIn: 'Tous les montants en {{currency}}',
        managementLegend: 'Rapport de gestion — non audité',
        ledger: {
            unattributedCurrency: 'Devise non attribuable',
            unattributedNote: "Les lignes du compte d'attente de change face à plusieurs devises ne peuvent être attribuées à une seule devise. Elles figurent à part, sans symbole monétaire.",
            filteredFunds: 'Filtré sur {{count}} fonds',
            unallocatedOnly: 'Non affecté uniquement — lignes sans fonds',
            fundTaggingDifference: "Écart d'affectation aux fonds",
            fundTaggingNote: "Avec un filtre de fonds, une écriture peut perdre une partie de ses lignes (la ligne bancaire n'a pas de fonds, la ligne de charge en a un). L'écart d'affectation aux fonds est cet écart, affiché pour que les colonnes s'équilibrent.",
            account: 'Compte',
            debit: 'Débit',
            credit: 'Crédit',
            total: 'Total',
        },
        trialBalance: {
            title: 'Balance générale',
            opening: 'Soldes à nouveau',
            movement: 'Mouvements de la période',
            closing: 'Soldes de clôture',
            categories: {
                assets: 'Actif',
                liabilities: 'Dettes',
                equity: 'Capitaux propres',
                revenue: 'Produits',
                expenses: 'Charges',
            },
            priorResult: 'Résultat cumulé au {{date}} (avant affectation)',
            empty: 'Aucune écriture sur cette période.',
            notBalancedTitle: "Cette balance générale n'est pas équilibrée",
            notBalancedLine: '{{column}} : débit {{debit}}, crédit {{credit}}',
            notBalancedBody: 'Le total des débits et le total des crédits doivent être égaux. Vérifiez les écritures déséquilibrées dans le grand livre.',
            fxClearingNote: "Les comptes d'attente de change figurent ici ; le bilan les exclut.",
            suspenseNote: "Les comptes d'attente figurent ici et au bilan.",
            suspenseWarningTitle: "Solde du compte d'attente : {{amount}}",
            suspenseWarningBody: 'Il s’agit de mouvements non catégorisés. Catégorisez-les avant de vous fier à ces chiffres.',
            workingDocumentNote: "Les comptes de produits et de charges s'ouvrent à zéro ; leur résultat antérieur à la période figure sur une ligne de capitaux propres. Une balance générale est un document de travail, pas un état légal.",
        },
        cashFlow: {
            title: 'Tableau des flux de trésorerie (méthode directe)',
            activities: {
                operating: 'Flux de trésorerie liés aux activités opérationnelles',
                investing: "Flux de trésorerie liés aux activités d'investissement",
                financing: 'Flux de trésorerie liés aux activités de financement',
            },
            netActivities: {
                operating: 'Flux net de trésorerie lié aux activités opérationnelles',
                investing: "Flux net de trésorerie lié aux activités d'investissement",
                financing: 'Flux net de trésorerie lié aux activités de financement',
            },
            sections: {
                operating_customers: 'Encaissements des clients et des financeurs',
                operating_revenue: 'Autres encaissements de produits',
                operating_suppliers: 'Paiements aux fournisseurs',
                operating_expenses: 'Charges payées',
                operating_interest_received: 'Intérêts reçus',
                operating_interest_paid: 'Intérêts payés',
                operating_income_tax: 'Impôts sur le résultat payés',
                operating_vat_taxes: 'TVA et autres impôts et taxes',
                operating_payroll_other: 'Salaires et autres dettes à court terme',
                operating_other_current_assets: 'Autres actifs circulants',
                operating_credit_cards: 'Règlements de cartes de crédit',
                operating_deferred_revenue: "Produits constatés d'avance",
                operating_deposits_provisions: 'Dépôts et provisions',
                investing: "Acquisitions et cessions d'immobilisations",
                financing: 'Emprunts, capitaux propres et comptes courants des associés',
                outside_fx_transfers: "Mouvements du compte d'attente de change (virements entre comptes en devises différentes et règlements en devises)",
                cash: 'Autres comptes de trésorerie',
                outside_opening_balances: "Soldes d'ouverture comptabilisés",
                outside_suspense: "Compte d'attente (non catégorisé)",
                unattributed: 'Devise non attribuable',
                unmapped: 'Comptes non classés',
                fund_tagging_difference: 'Mouvements de trésorerie des autres fonds et des lignes non affectées',
            },
            directions: {
                investing: { outflow: 'Acquisitions', inflow: 'Cessions' },
                financing: { outflow: 'Remboursements et autres paiements', inflow: 'Tirages et autres encaissements' },
            },
            otherSection: 'Autre ({{key}})',
            netCashFromActivities: 'Flux de trésorerie des activités',
            reconciling: 'Éléments de rapprochement hors activités',
            totalReconciling: 'Total des éléments de rapprochement',
            netChange: 'Variation de trésorerie',
            openingCash: "Trésorerie à l'ouverture",
            closingCash: 'Trésorerie à la clôture',
            openingCashAt: "Trésorerie à l'ouverture ({{date}})",
            closingCashAt: 'Trésorerie à la clôture ({{date}})',
            empty: 'Aucun compte bancaire ni flux de trésorerie sur cette période.',
            notReconciledTitle: "La trésorerie d'ouverture plus la variation n'est pas égale à la trésorerie de clôture",
            notReconciledPriorTitle: "Période précédente {{start}} – {{end}} : la trésorerie d'ouverture plus la variation n'est pas égale à la trésorerie de clôture",
            notReconciledBody: 'Vérifiez dans le grand livre les écritures qui mouvementent la trésorerie sans contrepartie.',
            comparativeUnavailableTitle: "La période comparative {{start}} – {{end}} n'a pas pu être récupérée",
            comparativeUnavailableBody: 'Ce tableau ne présente que la période en cours. Les chiffres affichés sont complets pour cette période.',
            notes: {
                cash: 'La trésorerie est le solde des comptes bancaires et de caisse, y compris un solde débiteur. Un compte de découvert distinct figure sous les activités de financement.',
                nonCash: 'Les opérations sans mouvement de trésorerie sont exclues.',
                vatAndCards: "La TVA ou la taxe sur les ventes afférente à une vente ou à un achat figure avec l'élément opérationnel payé ou encaissé dans la même transaction ; à défaut (une immobilisation, un emprunt, des intérêts ou l'impôt sur les bénéfices), elle figure sous les paiements aux fournisseurs ou les encaissements clients, de sorte que les activités d'investissement et de financement sont présentées hors TVA. TVA et autres impôts et taxes présente les règlements et remboursements de TVA et de taxe sur les ventes ainsi que les paiements d'autres impôts et taxes. Un achat par carte figure lors du règlement de la carte.",
                lookThrough: "Le paiement d'une facture est classé selon les lignes de la facture : la part relative à une immobilisation, un placement, un emprunt ou un autre financement, des intérêts ou l'impôt sur les bénéfices figure sous cette activité ; le reste, TVA comprise, reste sous les encaissements clients ou les paiements aux fournisseurs. Les règlements de carte de crédit et les versements des canaux de vente ne sont pas ventilés ainsi et restent sur leur propre ligne.",
                disposals: "Les plus-values et moins-values de cession d'une immobilisation figurent sous les activités d'investissement, de sorte que le produit d'une cession est présenté en totalité.",
                fxClearing: "Un paiement effectué depuis un compte bancaire dans une autre devise (par exemple un emprunt remboursé depuis le compte en USD) figure sous les mouvements du compte d'attente de change dans la devise de paiement, et sous son activité dans la devise du compte.",
                fundTaggingDifference: "La trésorerie d'ouverture et de clôture est celle de toute l'organisation. Avec un filtre par fonds, cette ligne représente les mouvements de trésorerie de tous les autres fonds et des lignes sans fonds, de sorte que le tableau reste rapproché ; elle ne signale pas une erreur d'affectation. Affecter la ligne bancaire à un fonds n'y change rien.",
            },
        },
        openItems: {
            title: { ar: 'Balance âgée clients', ap: 'Balance âgée fournisseurs' },
            contact: { ar: 'Client', ap: 'Fournisseur' },
            invoice: 'Facture',
            invoiceDate: 'Date de facture',
            dueDate: "Date d'échéance",
            daysOverdue: 'Jours de retard',
            invoiceTotal: 'Montant de la facture',
            outstanding: 'Reste dû',
            noContact: 'Sans contact',
            contactTotal: 'Total {{name}}',
            agingTitle: 'Antériorité',
            buckets: {
                current: 'Non échu',
                '1-30': '1–30 jours',
                '31-60': '31–60 jours',
                '61-90': '61–90 jours',
                '90+': 'Plus de 90 jours',
            },
            total: 'Total',
            subtotals: {
                ar: {
                    debitBalances: 'Soldes débiteurs',
                    creditBalances: 'Soldes créditeurs (trop-perçus)',
                    net: 'Net à recevoir',
                },
                ap: {
                    debitBalances: 'Soldes débiteurs (trop-payés)',
                    creditBalances: 'Soldes créditeurs',
                    net: 'Net à payer',
                },
            },
            tieOut: {
                title: 'Rapprochement avec le grand livre',
                account: 'Compte collectif',
                listNet: 'Éléments ouverts',
                openingBalances: "Soldes d'ouverture",
                manualJournals: 'Autres écritures',
                ledgerBalance: 'Grand livre',
                unexplained: 'Inexpliqué',
                unexplainedTitle: 'Une partie du solde du grand livre est inexpliquée',
                unexplainedBody: "Les éléments ouverts, les soldes d'ouverture et les autres écritures n'égalent pas le solde du grand livre. Vérifiez le compte collectif.",
            },
            empty: 'Aucun élément ouvert au {{date}}.',
            ledgerNote: "Établie à partir du grand livre : une facture est ouverte lorsque les lignes qui y sont comptabilisées jusqu'à cette date ne se soldent pas à zéro, quel que soit son statut aujourd'hui. L'antériorité du tableau de bord est une vue de gestion fondée sur le statut et peut différer.",
            editLimitNote: "La modification d'une facture émise la recomptabilise sur place : une liste établie pour une date passée peut donc changer par la suite.",
        },
        balanceSheet: {
            title: 'Bilan',
            assets: 'Actif',
            currentAssets: 'Actif circulant',
            nonCurrentAssets: 'Actif immobilisé',
            totalCurrentAssets: 'Total actif circulant',
            totalNonCurrentAssets: 'Total actif immobilisé',
            totalAssets: 'Total actif',
            liabilitiesAndEquity: 'Passif et capitaux propres',
            currentLiabilities: 'Dettes à court terme',
            nonCurrentLiabilities: 'Dettes à long terme',
            totalCurrentLiabilities: 'Total dettes à court terme',
            totalNonCurrentLiabilities: 'Total dettes à long terme',
            totalLiabilities: 'Total des dettes',
            equity: 'Capitaux propres',
            totalEquity: 'Total capitaux propres',
            totalLiabilitiesAndEquity: 'Total passif et capitaux propres',
            beforeAppropriation: 'Avant affectation du résultat',
            noCurrentAssets: "Aucun actif circulant",
            noNonCurrentAssets: 'Aucun actif immobilisé',
            noCurrentLiabilities: 'Aucune dette à court terme',
            noNonCurrentLiabilities: 'Aucune dette à long terme',
            noEquity: 'Aucun capital propre',
            noteTitle: 'Note sur le bilan',
            noteBody: "Le résultat net de l'exercice en cours est inclus dans les capitaux propres pour les besoins du bilan. À la clôture, les comptes de produits et de charges seront soldés vers les réserves / actifs nets.",
            balanceCheck: "Contrôle d'équilibre",
            notBalanced: "Le bilan n'est PAS équilibré",
            notBalancedPrior: "Le bilan comparatif au {{date}} n'est PAS équilibré",
            liabilitiesPlusEquity: 'Passif + capitaux propres',
            unexplainedDifference: 'Écart inexpliqué',
            notBalancedBody: "Cet écart n'est pas un ajustement de conversion de devises — les comptes sous-jacents sont déséquilibrés. Vérifiez les écritures récentes et les soldes d'ouverture, ou contactez le support.",
            missingRatesTitle: 'Taux de change manquants : {{currencies}}',
            missingRatesTitlePrior: 'Taux de change manquants au {{date}} : {{currencies}}',
            missingRatesBody: "Les montants dans ces devises sont affichés sans conversion (1:1). Les totaux convertis et l'ajustement cumulé de conversion ne sont pas fiables tant que les taux ne sont pas disponibles pour la date de clôture.",
            comparativeUnavailableTitle: "Les chiffres comparatifs au {{date}} n'ont pas pu être récupérés pour ce rapport",
            comparativeUnavailableBody: "L'état présente uniquement la période en cours.",
        },
        translation: {
            title: 'Informations sur la conversion des devises étrangères',
            subtitle: 'Selon IFRS pour PME section 30.25-30.26',
            presentationCurrency: 'Devise de présentation',
            closingDate: 'Date de clôture',
            method: 'Méthode de conversion',
            methodClosingRate: 'Méthode du cours de clôture (IFRS pour PME 30.18)',
            functionalCurrencies: 'Devises fonctionnelles converties',
            ratesTitle: 'Cours de clôture appliqués',
            from: 'De',
            to: 'Vers',
            rate: 'Cours',
            rateDate: 'Date du cours',
            source: 'Source : Banque centrale européenne (BCE)',
            cta: 'Ajustement cumulé de conversion',
            ctaNote: 'Les écarts de conversion sont comptabilisés en capitaux propres selon IFRS pour PME 30.18(c)',
        },
        incomeStatement: {
            title: 'Compte de résultat',
            titleSuffixUnallocated: '— Transactions non affectées',
            titleSuffixFundingSourceView: '— Vue par source de financement',
            titleSuffixFundingSources: '— {{count}} sources de financement',
            revenue: 'Produits',
            totalRevenue: 'Total des produits',
            costOfSales: 'Coût des ventes',
            totalCostOfSales: 'Total coût des ventes',
            grossProfit: 'Marge brute',
            operatingExpenses: "Charges d'exploitation",
            totalOperatingExpenses: "Total charges d'exploitation",
            employeeBenefits: 'Charges de personnel',
            depreciationAmortization: 'Amortissements et dépréciations',
            rawMaterials: 'Matières premières et consommables',
            otherOperatingExpenses: "Autres charges d'exploitation",
            operatingProfit: "Résultat d'exploitation",
            financeCosts: 'Charges financières',
            totalFinanceCosts: 'Total charges financières',
            profitBeforeTax: 'Résultat avant impôt',
            taxExpense: "Charge d'impôt",
            profitForPeriod: "Résultat de l'exercice",
            netProfitCaption: 'Bénéfice net de la période',
            netLossCaption: 'Perte nette de la période',
            directTransactions: 'Transactions directes',
            account: 'Compte',
            totalColumn: 'Total',
            budget: 'Budget : {{amount}}',
            expenses: 'Charges',
            totalExpenses: 'Total des charges',
            comparisonNote: 'Ce compte de résultat compare {{count}} projets pour la période du {{start}} au {{end}}.',
            periodNote: 'Ce compte de résultat récapitule les produits et charges pour la période du {{start}} au {{end}}.',
            ifrsNote: 'Établi conformément à IFRS pour PME section 5 (charges classées par nature)',
            filteredProjects: 'Filtré par {{count}} projet(s)',
            filteredFunds: 'Filtré par {{count}} source(s) de financement',
            unallocatedOnly: 'Affichage des transactions non affectées uniquement',
            translationNoteTitle: 'Note de conversion des devises (IFRS PME section 30)',
            translationNoteBody: 'Montants convertis en {{currency}} aux cours de clôture du {{date}}.',
            translationNoteBodyPrior: 'Montants comparatifs convertis en {{currency}} aux cours de clôture du {{date}}.',
            translationFunctionalCurrencies: 'Devises fonctionnelles : {{currencies}}',
            translationMissingRates: 'Note : taux de change indisponibles pour : {{currencies}}',
            translationMissingRatesPrior: 'Note : taux de change indisponibles pour la période comparative au {{date}} : {{currencies}}',
            comparativeUnavailableTitle: "Les chiffres comparatifs pour {{start}} — {{end}} n'ont pas pu être récupérés pour ce rapport",
            comparativeUnavailableBody: "L'état présente uniquement la période en cours.",
        },
        tax: {
            filingSupportLegend: "Rapport d'aide à la déclaration — non audité",
            vatReturnTitle: 'Déclaration de {{tax}}',
            salesTaxTitle: 'Rapport de taxe de vente',
            salesTaxName: 'Taxe de vente',
            outputSectionTitle: '{{tax}} collectée (ventes aux clients)',
            inputSectionTitle: '{{tax}} déductible (achats auprès des fournisseurs)',
            salesByRateTitle: 'Taxe collectée par taux',
            outputCard: '{{tax}} collectée',
            outputHint: 'Collectée sur les ventes',
            inputCard: '{{tax}} déductible',
            inputHint: 'Récupérable sur les achats',
            netCard: '{{tax}} nette',
            amountDueLabel: "Montant dû à l'administration fiscale",
            refundDueLabel: "Remboursement attendu de l'administration fiscale",
            balancedLabel: 'Équilibré — rien à régler',
            totalOutput: 'Total {{tax}} collectée',
            totalInput: 'Total {{tax}} déductible',
            totalTaxCollected: 'Total taxe collectée',
            totalSales: 'Total des ventes',
            taxCollected: 'Taxe collectée',
            rateName: 'Taux',
            rateColumn: 'Taux de {{tax}}',
            rateColumnGeneric: 'Taux de taxe',
            netAmount: 'Montant net',
            grossAmount: 'Montant brut',
            taxAmount: 'Montant de {{tax}}',
            taxAmountGeneric: 'Montant de taxe',
            reverseChargeSuffix: '(autoliquidation)',
            rateSubtotal: 'Sous-total {{rate}}',
            rateSubtotalExclReverseCharge: 'Sous-total {{rate}} (hors autoliquidation)',
            noEntries: "Aucune écriture de taxe pour cette période. Les données fiscales apparaîtront ici dès que des factures ou dépenses avec ventilation de taxe seront créées.",
            noSalesEntries: "Aucune écriture de taxe sur les ventes pour cette période. Les données fiscales apparaîtront ici dès que des factures avec ventilation de taxe seront créées.",
            summaryTitle: 'Résumé de paiement {{tax}}',
            amountDue: "Montant dû à l'administration fiscale :",
            refundDue: "Remboursement attendu de l'administration fiscale :",
            nilReturnAmount: 'Montant net :',
            taxCollectedToRemit: 'Taxe collectée à reverser :',
            notOnReturnLabel: 'Autres mouvements du compte Taxe de vente à payer (hors déclaration) :',
            notOnReturnHint: "Paiements à l'administration fiscale et lignes sans taux de vente. Affiché pour que ce rapport concorde avec le compte Taxe de vente à payer ; non inclus dans la taxe à reverser.",
            section: {
                sales_tax_payable: 'Taxe de vente et d\'utilisation',
                meals_rooms_tax_payable: 'Taxe sur les repas et l\'hébergement',
                other: 'Autre taxe',
            },
            sectionWithCode: '{{title}} ({{code}})',
            category: { sales: 'Ventes', meals: 'Repas', rooms: 'Hébergement', alcohol: 'Alcool', other: 'Autre' },
            categoryColumn: 'Catégorie',
            sectionTotal: 'Total de la section',
            stateReturnTotal: 'Total de la taxe sur la déclaration de l\'État',
            localOptionTitle: 'Taxe locale optionnelle par commune',
            localOptionHint: 'Incluse dans la déclaration de l\'État ci-dessus et déclarée par commune.',
            townColumn: 'Commune',
            localTaxColumn: 'Taxe locale',
            localOptionTotal: 'Total de la taxe locale optionnelle',
            cityTitle: 'Versé à la ville de {{city}}',
            cityHint: 'Perçue pour la ville et versée directement à celle-ci. Hors déclaration de l\'État.',
            cityTotal: 'Total versé à la ville',
            ofWhichStateReturn: 'dont sur la déclaration de l\'État',
            ofWhichCity: 'dont versé à la ville de {{city}}',
            forPeriodEnding: 'Pour la période se terminant le {{end}}',
            complianceLine1: 'Ce rapport a été généré à des fins de conformité fiscale.',
            complianceLine2: "Veuillez vérifier tous les montants avant transmission à l'administration fiscale.",
        },
        volunteer: {
            title: 'Rapport des bénévoles',
            subtitle: '{{project}} · {{from}} – {{to}}',
            signupPage: "Page d'inscription : {{title}} · {{status}}",
            pageStatus: { draft: 'Brouillon', open: 'Ouverte', closed: 'Fermée' },
            noShifts: "Ce projet n'a pas encore de créneaux bénévoles.",
            summary: {
                title: 'Synthèse',
                people: 'Bénévoles inscrits',
                peopleHint: 'Personnes, chacune comptée une fois',
                filled: 'Places pourvues',
                filledHint: '{{pct}} de toutes les places',
                open: 'Places libres',
                openHint: 'Créneaux au total : {{count}}',
                empty: 'Créneaux sans personne',
                emptyHint: 'Créneaux complets : {{count}}',
                confirmed: 'Confirmés',
                pending: "En attente d'approbation",
                waitlisted: "En liste d'attente",
                multiShift: 'Bénévoles sur plusieurs créneaux',
            },
            allRoles: 'Tous les rôles',
            allDays: 'Tous les jours',
            byRole: {
                title: 'Par rôle',
                caption: 'Places confirmées par rapport au total des places',
                role: 'Rôle',
                shifts: 'Créneaux',
                full: 'Complets',
                filled: 'Pourvues / places',
                fill: 'Remplissage',
                open: 'Libres',
                waitlist: "Liste d'attente",
            },
            byRoleAndShift: {
                title: 'Par rôle et par créneau',
                caption: 'Chaque créneau, par ordre chronologique au sein de son rôle',
                shiftsCount: 'Créneaux : {{count}} · {{confirmed}} / {{capacity}} pourvues',
                day: 'Jour',
                time: 'Horaire',
                shift: 'Créneau',
                place: 'Lieu',
                filled: 'Pourvues / places',
                status: 'Statut',
                full: 'Complet',
                noOneYet: 'Personne pour le moment',
                nOpen: 'Libres : {{count}}',
                nOnWaitlist: "En liste d'attente : {{count}}",
                nAwaitingApproval: "En attente d'approbation : {{count}}",
                held: "Réservé pour approbation",
                nextDay: '+1',
                nextDayNote: '+1 = se termine le lendemain',
            },
            byDay: {
                title: 'Par jour',
                caption: 'Créneaux regroupés selon leur date de début',
                day: 'Jour',
                shifts: 'Créneaux',
                volunteers: 'Bénévoles',
                filled: 'Pourvues / places',
                fill: 'Remplissage',
                open: 'Libres',
                lede: "Les bénévoles par jour comptent chaque personne une seule fois ce jour-là, même si elle fait deux créneaux.",
            },
            matrix: {
                title: 'Rôle × jour',
                caption: 'Places pourvues / places pour chaque rôle et chaque jour',
                role: 'Rôle',
                legend: {
                    empty: 'Personne pour le moment',
                    under: 'Moins de {{pct}}',
                    most: '{{pct}} ou plus',
                    full: 'Complet',
                    none: 'Aucun créneau ce jour-là',
                },
            },
            notes: {
                countingRule: "Les places pourvues ne comptent que les places confirmées. Les places annulées ne sont pas comptées. Les places en liste d'attente et en attente d'approbation sont indiquées à part ; une place en attente d'approbation est réservée, elle n'est donc pas libre.",
            },
        },
    },
};
//# sourceMappingURL=fr.js.map